import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert 
} from 'react-bootstrap';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';
import { authAPI } from '../services/api';
import { Formik } from 'formik';
import * as Yup from 'yup';

interface UserProfileValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

// Validation schema
const ProfileSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .test('empty-or-min-length', 'Password must be at least 6 characters', function(value) {
      // If password is empty, it's valid (not updating password)
      return !value || value.length >= 6;
    }),
  confirmPassword: Yup.string()
    .test('passwords-match', 'Passwords must match', function(value) {
      return !this.parent.password || value === this.parent.password;
    }),
});

const UserProfile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialValues, setInitialValues] = useState<UserProfileValues>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await authAPI.getUserProfile();
        const userData = response.data;
        
        setInitialValues({
          name: userData.name || '',
          email: userData.email || '',
          password: '',
          confirmPassword: '',
        });
        setLoading(false);
      } catch (err: any) {
        setError('Failed to fetch profile data');
        console.error(err);
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle form submission
  const handleSubmit = async (values: UserProfileValues, { setSubmitting }: any) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      // Only include password in update if it's not empty
      const updateData = {
        name: values.name,
        email: values.email,
        ...(values.password ? { password: values.password } : {})
      };
      
      await authAPI.updateUserProfile(updateData);
      setSuccess('Profile updated successfully');
      
      // Reset password fields
      setInitialValues({
        name: values.name,
        email: values.email,
        password: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profile data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1 className="mb-4">My Profile</h1>

      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0">Profile Information</h5>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Formik
                initialValues={initialValues}
                validationSchema={ProfileSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row className="mb-4">
                      <Col md={12} className="text-center mb-4">
                        <div 
                          className="mx-auto rounded-circle bg-light d-flex align-items-center justify-content-center"
                          style={{ width: '100px', height: '100px' }}
                        >
                          <Icon icon={Icons.FaUser} className="text-secondary" style={{ fontSize: '2.5rem' }} />
                        </div>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <hr className="my-4" />
                    <h6 className="mb-3">Change Password</h6>
                    <p className="text-muted mb-3">Leave blank if you don't want to change your password</p>

                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && !!errors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-end">
                      <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={isSubmitting}
                      >
                        <Icon icon={Icons.FaSave} className="me-2" />
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile; 